// import { pdfService } from './pdfService'; // 不再使用pdfService
import { openaiService } from './openaiService';
import { policyNameOptimizer } from './policyNameOptimizer';

export interface PolicyAnalysisResult {
  policyName: string;
  monthlyPremium: number; // 改為數字類型
  annualPremium?: number; // 新增：年繳保費
  companyName: string;
  policyType?: string;
  coverageAmount?: string;
  effectiveDate?: string;
  expiryDate?: string;
  policyNumber?: string;
  confidence: number; // 0-1 之間的可信度分數
  // 新增保費單位和狀態字段
  premiumUnit?: 'monthly' | 'one-time'; // 保費單位：月繳或一次性
  status?: 'active' | 'expired'; // 保單狀態：保障中或已到期
}

class AIAnalysisService {
  private analysisCache = new Map<string, PolicyAnalysisResult>();
  
  // AI分析PDF文本
  async analyzePolicyText(text: string): Promise<PolicyAnalysisResult> {
    // 創建文本的哈希值作為緩存鍵
    const textHash = this.createTextHash(text);
    
    console.log('AI分析文本哈希:', textHash);
    console.log('文本長度:', text.length);
    console.log('文本預覽:', text.substring(0, 100) + '...');
    
    // 檢查緩存
    if (this.analysisCache.has(textHash)) {
      console.log('使用緩存的分析結果');
      return this.analysisCache.get(textHash)!;
    }
    try {
      console.log('開始AI分析保單文本...');
      
      // 首先嘗試使用OpenAI API
      if (openaiService.isConfigured()) {
        console.log('使用OpenAI API進行分析...');
        const openaiResult = await openaiService.analyzePolicyText(text);
        
        // 轉換OpenAI結果格式
        // 使用AI返回的premiumPaymentType，如果沒有則使用analyzePremiumUnit判斷
        const premiumUnit = openaiResult.premiumPaymentType === 'one-time' 
          ? 'one-time' 
          : (openaiResult.premiumPaymentType === 'annual' ? 'monthly' : this.analyzePremiumUnit(text, openaiResult.policyName));
        
        const result: PolicyAnalysisResult = {
          policyName: openaiResult.policyName,
          monthlyPremium: openaiResult.monthlyPremium,
          annualPremium: openaiResult.annualPremium,
          companyName: openaiResult.companyName,
          policyType: openaiResult.policyType,
          coverageAmount: openaiResult.coverageAmount,
          effectiveDate: openaiResult.effectiveDate,
          expiryDate: openaiResult.expiryDate, // 新增：傳遞到期日期
          policyNumber: openaiResult.policyNumber,
          confidence: openaiResult.confidence,
          // 分析保費單位和狀態
          premiumUnit: premiumUnit,
          status: this.analyzePolicyStatus(text, openaiResult.effectiveDate, openaiResult.expiryDate)
        };

        // 優化保單名稱，移除與保險公司名稱重疊的部分
        const optimizationResult = policyNameOptimizer.optimizePolicyName(
          result.policyName, 
          result.companyName
        );
        
        if (optimizationResult.confidence > 0.3) {
          result.policyName = optimizationResult.optimizedName;
          console.log('保單名稱已優化:', {
            原名: optimizationResult.originalName,
            優化後: optimizationResult.optimizedName,
            移除部分: optimizationResult.removedParts,
            可信度: optimizationResult.confidence
          });
        }
        
        console.log('OpenAI分析完成:', result);
        
        // 保存到緩存
        this.analysisCache.set(textHash, result);
        return result;
      } else {
        console.log('OpenAI API未配置，使用備用分析...');
        // 備用方案：使用本地分析
        const analysis = this.performTextAnalysis(text);
        console.log('備用分析完成:', analysis);
        
        // 保存到緩存
        this.analysisCache.set(textHash, analysis);
        return analysis;
      }
    } catch (error: any) {
      console.error('AI分析失敗:', error.message);
      
      // 如果OpenAI API失敗，使用備用分析
      console.log('OpenAI API失敗，使用備用分析...');
      try {
        const fallbackAnalysis = this.performTextAnalysis(text);
        
        // 對備用分析結果也進行名稱優化
        const optimizationResult = policyNameOptimizer.optimizePolicyName(
          fallbackAnalysis.policyName, 
          fallbackAnalysis.companyName
        );
        
        if (optimizationResult.confidence > 0.3) {
          fallbackAnalysis.policyName = optimizationResult.optimizedName;
          console.log('備用分析保單名稱已優化:', {
            原名: optimizationResult.originalName,
            優化後: optimizationResult.optimizedName,
            移除部分: optimizationResult.removedParts,
            可信度: optimizationResult.confidence
          });
        }
        
        console.log('備用分析完成:', fallbackAnalysis);
        return fallbackAnalysis;
      } catch (fallbackError: any) {
        console.error('備用分析也失敗:', fallbackError.message);
        throw new Error(`AI分析失敗: ${error.message}`);
      }
    }
  }

  // 創建文本哈希值
  private createTextHash(text: string): string {
    // 簡單的哈希函數，用於緩存鍵
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // 轉換為32位整數
    }
    return hash.toString();
  }

  // 執行文本分析邏輯
  private performTextAnalysis(text: string): PolicyAnalysisResult {
    const cleanText = text.toLowerCase();
    
    // 分析保單類型
    const policyType = this.analyzePolicyType(cleanText);
    
    // 分析保險公司
    const companyName = this.analyzeCompanyName(cleanText);
    
    // 分析保單名稱
    const policyName = this.analyzePolicyName(cleanText, policyType);
    
    // 分析保費（返回字符串，需要轉換為數字）
    const monthlyPremiumStr = this.analyzePremium(cleanText);
    const monthlyPremium = parseFloat(monthlyPremiumStr.replace(/[NT$,\s]/g, '')) || 0;
    
    // 分析保障金額
    const coverageAmount = this.analyzeCoverageAmount(cleanText);
    
    // 分析日期
    const dates = this.analyzeDates(cleanText);
    
    // 計算可信度
    const confidence = this.calculateConfidence(policyName, companyName, monthlyPremiumStr);
    
    return {
      policyName,
      monthlyPremium, // 現在是數字類型
      companyName,
      policyType,
      coverageAmount,
      effectiveDate: dates.effectiveDate,
      expiryDate: dates.expiryDate,
      policyNumber: this.analyzePolicyNumber(cleanText),
      confidence,
      // 分析保費單位和狀態
      premiumUnit: this.analyzePremiumUnit(text, policyName),
      status: this.analyzePolicyStatus(text, dates.effectiveDate, dates.expiryDate)
    };
  }

  // 分析保單類型
  private analyzePolicyType(text: string): string {
    if (text.includes('健康') || text.includes('醫療') || text.includes('health')) {
      return '健康保險';
    } else if (text.includes('人壽') || text.includes('life')) {
      return '人壽保險';
    } else if (text.includes('意外') || text.includes('accident')) {
      return '意外保險';
    } else if (text.includes('車險') || text.includes('汽車') || text.includes('auto')) {
      return '汽車保險';
    } else if (text.includes('火險') || text.includes('火災') || text.includes('fire')) {
      return '火災保險';
    } else if (text.includes('旅遊') || text.includes('travel')) {
      return '旅遊保險';
    } else if (text.includes('寵物') || text.includes('pet')) {
      return '寵物保險';
    }
    return '綜合保險';
  }

  // 分析保險公司名稱
  private analyzeCompanyName(text: string): string {
    
    // 常見台灣保險公司
    const companies = [
      '富邦', '國泰', '新光', '南山', '中國人壽', '台灣人壽', '全球人壽',
      '元大人壽', '三商美邦', '遠雄人壽', '安聯人壽', '保誠人壽',
      '友邦人壽', '宏泰人壽', '台新人壽', '第一金人壽', '合庫人壽'
    ];
    
    for (const company of companies) {
      if (text.includes(company.toLowerCase())) {
        return company + '人壽';
      }
    }
    
    return '未知保險公司';
  }

  // 分析保單名稱
  private analyzePolicyName(text: string, policyType: string): string {
    // 嘗試從文本中提取保單名稱
    const namePatterns = [
      /保單名稱[：:]\s*([^\n\r]+)/i,
      /產品名稱[：:]\s*([^\n\r]+)/i,
      /保險商品[：:]\s*([^\n\r]+)/i,
      /plan\s*name[：:]\s*([^\n\r]+)/i
    ];
    
    for (const pattern of namePatterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        return match[1].trim();
      }
    }
    
    // 如果找不到具體名稱，使用類型作為名稱
    return `${policyType}保單`;
  }

  // 分析保費
  private analyzePremium(text: string): string {
    
    // 查找保費相關信息
    const premiumPatterns = [
      /月繳保費[：:]\s*(?:NT\$?|新台幣)?\s*([0-9,]+)/i,
      /每月保費[：:]\s*(?:NT\$?|新台幣)?\s*([0-9,]+)/i,
      /monthly\s*premium[：:]\s*(?:NT\$?)?\s*([0-9,]+)/i,
      /保費[：:]\s*(?:NT\$?|新台幣)?\s*([0-9,]+)/i
    ];
    
    for (const pattern of premiumPatterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        return `NT$${match[1]}`;
      }
    }
    
    return 'NT$0';
  }

  // 分析保障金額
  private analyzeCoverageAmount(text: string): string {
    const coveragePatterns = [
      /保險金額[：:]\s*(?:NT\$?|新台幣)?\s*([0-9,]+)/i,
      /保障金額[：:]\s*(?:NT\$?|新台幣)?\s*([0-9,]+)/i,
      /coverage\s*amount[：:]\s*(?:NT\$?)?\s*([0-9,]+)/i
    ];
    
    for (const pattern of coveragePatterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        return `NT$${match[1]}`;
      }
    }
    
    return 'NT$0';
  }


  // 分析保單號碼
  private analyzePolicyNumber(text: string): string {
    const policyNumberPatterns = [
      /保單號碼[：:]\s*([A-Z0-9]+)/i,
      /保單號[：:]\s*([A-Z0-9]+)/i,
      /policy\s*number[：:]\s*([A-Z0-9]+)/i,
      /contract\s*number[：:]\s*([A-Z0-9]+)/i
    ];
    
    for (const pattern of policyNumberPatterns) {
      const match = text.match(pattern);
      if (match) {
        return match[1];
      }
    }
    
    return 'N/A';
  }

  // 分析日期
  private analyzeDates(text: string): { effectiveDate: string; expiryDate: string } {
    const datePatterns = [
      /(\d{4}[-/]\d{1,2}[-/]\d{1,2})/g,
      /(\d{1,2}[-/]\d{1,2}[-/]\d{4})/g
    ];
    
    const dates: string[] = [];
    for (const pattern of datePatterns) {
      const matches = text.match(pattern);
      if (matches) {
        dates.push(...matches);
      }
    }
    
    const effectiveDate = dates[0] || new Date().toISOString().split('T')[0];
    const expiryDate = dates[1] || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    return { effectiveDate, expiryDate };
  }

  // 計算分析可信度
  private calculateConfidence(policyName: string, companyName: string, monthlyPremium: string): number {
    let confidence = 0;
    
    // 基礎分數
    confidence += 0.2;
    
    // 如果有提取到的關鍵信息，增加可信度
    if (policyName && policyName !== '未知保單') confidence += 0.2;
    if (companyName && companyName !== '未知保險公司') confidence += 0.2;
    if (monthlyPremium && monthlyPremium !== 'NT$0') confidence += 0.2;
    
    // 如果分析結果合理，增加可信度
    if (policyName && policyName !== '未知保險公司') confidence += 0.05;
    if (companyName && companyName !== '未知保險公司') confidence += 0.05;
    if (monthlyPremium && monthlyPremium !== 'NT$0') confidence += 0.05;
    
    return Math.min(confidence, 1.0);
  }

  // 分析保費單位（月繳或一次性）
  // 重要：此函數的邏輯必須與 System Prompt 中的判斷標準保持一致
  // 只根據保單內容判斷，不根據保單名稱或保單類型判斷
  private analyzePremiumUnit(text: string, policyName: string): 'monthly' | 'one-time' {
    const lowerText = text.toLowerCase();
    
    // 一次性保單的判斷標準（從保單內容中尋找）
    // 必須是與保費繳納方式相關的關鍵字
    const oneTimeKeywords = [
      // 保費繳納方式相關
      '一次繳納', '一次繳付', '單次保費', '總保費',
      'one time payment', 'single payment', 'lump sum',
      // 保障期間相關（短期）
      '保障期間', '保險期間', '承保期間',
      // 明確提到一次性、單次、短期（與保費相關）
      '一次性', '單次', '短期'
    ];
    
    // 年繳/月繳保單的判斷標準（從保單內容中尋找）
    // 必須是與保費繳納方式相關的關鍵字
    const monthlyKeywords = [
      // 保費繳納方式相關
      '年繳', '月繳', '定期繳納', '每年', '每月',
      'annual', 'monthly', 'yearly', 'quarterly',
      // 續保相關
      '續保', '續期', '定期保險'
    ];
    
    // 優先檢查一次性保單關鍵字（只檢查文本內容，不檢查保單名稱）
    for (const keyword of oneTimeKeywords) {
      if (lowerText.includes(keyword.toLowerCase())) {
        return 'one-time';
      }
    }
    
    // 檢查年繳/月繳關鍵字（只檢查文本內容，不檢查保單名稱）
    for (const keyword of monthlyKeywords) {
      if (lowerText.includes(keyword.toLowerCase())) {
        return 'monthly';
      }
    }
    
    // 默認為月繳（年繳型保單）
    return 'monthly';
  }

  // 分析保單狀態（保障中或已到期）
  private analyzePolicyStatus(text: string, effectiveDate?: string, expiryDate?: string): 'active' | 'expired' {
    const currentDate = new Date();
    
    console.log(`🔍 開始分析保單狀態:`);
    console.log(`   - expiryDate: ${expiryDate || '未提供'}`);
    console.log(`   - effectiveDate: ${effectiveDate || '未提供'}`);
    console.log(`   - 當前日期: ${currentDate.toISOString().split('T')[0]}`);
    
    // 優先級1：如果有明確的到期日期，必須優先使用它來判斷
    if (expiryDate) {
      try {
        console.log(`📅 嘗試解析到期日期: "${expiryDate}"`);
        const expiry = this.parseDate(expiryDate);
        if (expiry) {
          const isExpired = expiry < currentDate;
          console.log(`✅ 到期日期解析成功: ${expiryDate} -> ${expiry.toISOString().split('T')[0]}`);
          console.log(`   比較結果: ${expiry.toISOString().split('T')[0]} ${isExpired ? '<' : '>='} ${currentDate.toISOString().split('T')[0]}`);
          console.log(`   最終狀態: ${isExpired ? '已到期' : '保障中'}`);
          return isExpired ? 'expired' : 'active';
        } else {
          // expiryDate 存在但解析失敗，記錄詳細錯誤但不使用 fallback
          console.error(`⚠️ 到期日期解析失敗: ${expiryDate}，但因為有明確的到期日期，保守判斷為保障中`);
          console.error(`   原始日期字符串: "${expiryDate}"`);
          console.error(`   解析結果: null`);
          // 當有明確的 expiryDate 但解析失敗時，保守判斷為保障中（避免誤判）
          return 'active';
        }
      } catch (error) {
        // expiryDate 存在但解析時發生異常，記錄詳細錯誤但不使用 fallback
        console.error(`⚠️ 到期日期解析異常: ${expiryDate}`, error);
        console.error(`   錯誤詳情:`, error);
        // 當有明確的 expiryDate 但解析失敗時，保守判斷為保障中（避免誤判）
        return 'active';
      }
    }
    
    console.log(`⚠️ 沒有到期日期，使用 fallback 邏輯（基於生效日期和保單類型）`);
    
    // 優先級2：只有在沒有明確的到期日期時，才基於生效日期和保單類型判斷
    if (effectiveDate) {
      try {
        const effective = this.parseDate(effectiveDate);
        if (effective) {
          const daysSinceEffective = Math.ceil((currentDate.getTime() - effective.getTime()) / (1000 * 3600 * 24));
          
          // 分析保單類型來判斷過期邏輯
          const policyType = this.analyzePolicyType(text.toLowerCase());
          const isOneTime = this.isOneTimePolicy(policyType, text.toLowerCase());
          
          if (isOneTime) {
            // 一次性保單：隔天就過期
            if (daysSinceEffective > 1) {
              console.log(`一次性保單已過期: 生效後 ${daysSinceEffective} 天 > 1天`);
              return 'expired';
            }
          } else {
            // 月繳型保單：需要分析保單年限
            const policyDuration = this.analyzePolicyDuration(text);
            if (policyDuration > 0) {
              const maxDays = policyDuration * 365; // 轉換為天數
              if (daysSinceEffective > maxDays) {
                console.log(`月繳型保單已過期: 生效後 ${daysSinceEffective} 天 > ${maxDays} 天 (${policyDuration}年)`);
                return 'expired';
              }
            }
          }
          
          console.log(`保單仍有效: 生效後 ${daysSinceEffective} 天`);
        }
      } catch (error) {
        console.warn('無法解析生效日期:', effectiveDate, error);
      }
    }
    
    // 分析文本中的日期信息
    const lowerText = text.toLowerCase();
    
    // 查找日期模式
    const datePatterns = [
      /(\d{4})[年\-/](\d{1,2})[月\-/](\d{1,2})[日]?/g,
      /(\d{1,2})[月\-/](\d{1,2})[日\-/](\d{4})/g,
      /(\d{4})[年\-/](\d{1,2})[月]/g
    ];
    
    for (const pattern of datePatterns) {
      const matches = lowerText.match(pattern);
      if (matches) {
        for (const match of matches) {
          try {
            const date = new Date(match);
            if (date > currentDate) {
              return 'active';
            }
          } catch (error) {
            // 忽略無法解析的日期
          }
        }
      }
    }
    
    // 查找關鍵字
    const expiredKeywords = [
      '已到期', 'expired', '過期', '失效', '終止', '結束',
      '到期日', 'expiry', 'valid until', 'expires on'
    ];
    
    const activeKeywords = [
      '有效', 'active', '生效', '保障中', '進行中', 'current',
      'valid', 'in force', 'effective'
    ];
    
    // 檢查過期關鍵字
    for (const keyword of expiredKeywords) {
      if (lowerText.includes(keyword)) {
        return 'expired';
      }
    }
    
    // 檢查有效關鍵字
    for (const keyword of activeKeywords) {
      if (lowerText.includes(keyword)) {
        return 'active';
      }
    }
    
    // 默認為保障中
    return 'active';
  }

  /**
   * 判斷是否為一次性保單
   */
  private isOneTimePolicy(policyType: string, text: string): boolean {
    const oneTimeKeywords = [
      '旅遊', 'travel', '短期', 'short term', '一次', 'one time', 
      '單次', 'single', '臨時', 'temporary', '活動', 'event',
      '運動', 'sport', '登山', 'hiking', '潛水', 'diving',
      '旅行', 'trip', '出差', 'business trip'
    ];
    
    // 檢查保單類型
    if (policyType.includes('旅遊') || policyType.includes('旅行')) {
      return true;
    }
    
    // 檢查文本關鍵字
    for (const keyword of oneTimeKeywords) {
      if (text.includes(keyword)) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * 分析保單年限
   */
  private analyzePolicyDuration(text: string): number {
    // 查找年限相關關鍵字
    const durationPatterns = [
      /(\d+)\s*年\s*期/g,
      /(\d+)\s*年期/g,
      /(\d+)\s*year/g,
      /(\d+)\s*年\s*保障/g,
      /(\d+)\s*年\s*保險/g,
      /期限[：:]\s*(\d+)\s*年/g,
      /保險期間[：:]\s*(\d+)\s*年/g
    ];
    
    for (const pattern of durationPatterns) {
      const matches = text.match(pattern);
      if (matches) {
        for (const match of matches) {
          const yearMatch = match.match(/(\d+)/);
          if (yearMatch) {
            const years = parseInt(yearMatch[1]);
            if (years > 0 && years <= 50) { // 合理的年限範圍
              console.log(`分析到保單年限: ${years}年`);
              return years;
            }
          }
        }
      }
    }
    
    // 如果找不到明確年限，根據保單類型推斷
    if (text.includes('終身') || text.includes('lifetime')) {
      return 99; // 終身保單
    } else if (text.includes('定期') || text.includes('term')) {
      return 20; // 定期保單，默認20年
    }
    
    return 0; // 無法確定年限
  }

  /**
   * 解析日期，支持民國年格式
   */
  private parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;
    
    try {
      // 處理民國年格式 (如: 113-11-12, 111-09-22, 170-09-22)
      // 支持2位數和3位數民國年
      const rocMatch = dateStr.match(/^(\d{2,3})[-/](\d{1,2})[-/](\d{1,2})$/);
      if (rocMatch) {
        const rocYear = parseInt(rocMatch[1]);
        const month = parseInt(rocMatch[2]);
        const day = parseInt(rocMatch[3]);
        
        // 判斷是否為民國年（民國年通常在 1-200 之間）
        // 如果年份小於等於200，且不是4位數，則視為民國年
        if (rocYear <= 200 && rocYear > 0) {
          const gregorianYear = rocYear + 1911; // 民國年轉西元年
          const date = new Date(gregorianYear, month - 1, day);
          console.log(`民國年日期解析: ${dateStr} -> ${gregorianYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`);
          return date;
        }
      }
      
      // 處理西元年格式 (如: 2024-11-12, 2024/11/12)
      if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(dateStr)) {
        return new Date(dateStr.replace(/\//g, '-'));
      }
      
      // 處理其他日期格式
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return null;
      }
      
      return date;
    } catch (error) {
      console.warn('日期解析失敗:', dateStr, error);
      return null;
    }
  }
}

export const aiAnalysisService = new AIAnalysisService();

